/**
 * KONFIGURACJA OBIEKTÓW DO NISZCZENIA — 5 biomów (Meadow / Mountain / Star Wars / Pixel World / Ocean)
 *
 * 30 typów destructibles (6 per biom). Dropią WYŁĄCZNIE walutę.
 *   - **coins** rolloane CLIENT-SIDE (do natychmiastowych coin popups,
 *     animacji, magnet). Klient sumuje i wysyła `coinsEarned` do `/world/tick`.
 *   - **gems** rolloane SERVER-SIDE (Etap 5, 5.0.B, anti-cheat). Drop rates
 *     żyją w `server/api/src/game-config/destructibles.ts` → `DESTRUCTIBLE_GEM_REWARDS`.
 *     Klient wysyła `destroyedByType: { 'dest-crystal': 3 }`, serwer rolluje.
 *     Frontend gem rewards = USUNIĘTE (martwy kod sprzed 5.0.B; jeśli kiedyś
 *     frontend będzie potrzebował znać gem rates, dodać endpoint
 *     `GET /config/destructibles` analogicznie do `/config/trading-plaza`).
 *
 * Pety i jajka nigdy z destructibles — tylko ze sklepu (patrz GAME_DESIGN §5.4).
 *
 * Krzywa balansu w Meadow:
 *   Stos monet S    →  50 HP /  8s  → 5–10 coins
 *   Stos monet M    → 200 HP / 15s  → 25–50 coins
 *   Stos monet L    → 800 HP / 30s  → 120–250 coins
 *   Skrzynia drewn. → 500 HP / 25s  → 60–100 coins
 *   Skrzynia kam.   → 2500 HP/ 60s  → 400–800 coins + 2% gem
 *   Kryształ        → 6000 HP/ 90s  → 1500–3000 coins + 8% gem
 *
 * Każdy biom ma WŁASNE destructible DEF-y z hardcoded HP/rewards. Mountain
 * (tier 2) destruktyble mają reward ×10 vs meadow zapisany wprost w defie
 * (NIE używamy `biome.coinMultiplier` w runtime — patrz BiomeDef komentarz).
 *
 * Każdy textureKey ma odpowiadający plik PNG w `assets/biomes/<biomeFolder>/destructibles/<key>.png`
 * + paired prompt .md w tym samym folderze. Snow/kolejne biomy będą miały
 * własne destructible DEFs z `biomeId` ustawionym na ich id.
 */

import halloween from '../../server/api/src/game-config/halloween.config.json';
import type { DestructibleDef } from '@/domain/types/destructible';

const BIOME = 'biome-meadow';
const BIOME_MOUNTAIN = 'biome-mountain';
const BIOME_STARWARS = 'biome-starwars';
const BIOME_PIXEL = 'biome-pixel';
const BIOME_OCEAN = 'biome-ocean';
const BIOME_FOOD = 'biome-food';
const BIOME_ATLANTIS = 'biome-atlantis';
const BIOME_SCHOOL = 'biome-school';
const BIOME_ARCTIC = 'biome-arctic';
const BIOME_EVENT_BEACH = 'biome-event-capibara-beach'; // event Wakacje Capibar (EV-1b)

export const DESTRUCTIBLES: DestructibleDef[] = [
  {
    id: 'dest-coin-pile-s',
    name: { pl: 'Stos Monet (Mały)', en: 'Coin Pile (Small)' },
    biomeId: BIOME,
    hpBase: 50,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 5, max: 10, chance: 1.0 },
    ],
    art: { textureKey: 'dest_coin_pile_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-coin-pile-m',
    name: { pl: 'Stos Monet (Średni)', en: 'Coin Pile (Medium)' },
    biomeId: BIOME,
    hpBase: 200,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 25, max: 50, chance: 1.0 },
    ],
    art: { textureKey: 'dest_coin_pile_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-coin-pile-l',
    name: { pl: 'Stos Monet (Duży)', en: 'Coin Pile (Large)' },
    biomeId: BIOME,
    hpBase: 800,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 120, max: 250, chance: 1.0 },
    ],
    art: { textureKey: 'dest_coin_pile_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-chest-wood',
    name: { pl: 'Skrzynia Drewniana', en: 'Wooden Chest' },
    biomeId: BIOME,
    hpBase: 500,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 60, max: 100, chance: 1.0 },
    ],
    art: { textureKey: 'dest_chest_wood', destroyFx: 'wood' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-chest-stone',
    name: { pl: 'Skrzynia Kamienna', en: 'Stone Chest' },
    biomeId: BIOME,
    hpBase: 2_500,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 400, max: 800, chance: 1.0 },
      // gems server-side: 2% × 1 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_chest_stone', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-crystal',
    name: { pl: 'Kryształ Łąkowy', en: 'Meadow Crystal' },
    biomeId: BIOME,
    hpBase: 6_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_500, max: 3_000, chance: 1.0 },
      // gems server-side: 8% × 1-2 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_crystal', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MOUNTAIN BIOME (tier 2) — HP ×10, coin reward ×10, gem chance bonus.
  // PNG w `assets/biomes/mountain/destructibles/`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-snow-coin-pile-s',
    name: { pl: 'Zaśnieżony Stos (Mały)', en: 'Snowy Coin Pile (Small)' },
    biomeId: BIOME_MOUNTAIN,
    hpBase: 500,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 50, max: 100, chance: 1.0 },
    ],
    art: { textureKey: 'dest_snow_coin_pile_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-snow-coin-pile-m',
    name: { pl: 'Zaśnieżony Stos (Średni)', en: 'Snowy Coin Pile (Medium)' },
    biomeId: BIOME_MOUNTAIN,
    hpBase: 2_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 250, max: 500, chance: 1.0 },
    ],
    art: { textureKey: 'dest_snow_coin_pile_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-snow-coin-pile-l',
    name: { pl: 'Zaśnieżony Stos (Duży)', en: 'Snowy Coin Pile (Large)' },
    biomeId: BIOME_MOUNTAIN,
    hpBase: 8_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_200, max: 2_500, chance: 1.0 },
    ],
    art: { textureKey: 'dest_snow_coin_pile_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-mountain-rock-chest',
    name: { pl: 'Skalna Skrzynia', en: 'Mountain Rock Chest' },
    biomeId: BIOME_MOUNTAIN,
    hpBase: 5_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 600, max: 1_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_mountain_rock_chest', destroyFx: 'stone' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-ice-chest',
    name: { pl: 'Lodowa Skrzynia', en: 'Ice Chest' },
    biomeId: BIOME_MOUNTAIN,
    hpBase: 25_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 4_000, max: 8_000, chance: 1.0 },
      // gems server-side: 4% × 1-2 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_ice_chest', destroyFx: 'crystal' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-ice-crystal',
    name: { pl: 'Lodowy Kryształ', en: 'Ancient Ice Crystal' },
    biomeId: BIOME_MOUNTAIN,
    hpBase: 60_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 15_000, max: 30_000, chance: 1.0 },
      // gems server-side: 12% × 2-4 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_ice_crystal', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DZIKI ZACHÓD (tier 3, dawniej Star Wars — ID/texture keys ZOSTAJĄ).
  // HP ×100, coin reward ×100 vs meadow (×10 vs mountain).
  // PNG w `assets/biomes/starwars/destructibles/`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-starwars-credit-pile-s',
    name: { pl: 'Stos Złota (Mały)', en: 'Gold Pile (Small)' },
    biomeId: BIOME_STARWARS,
    hpBase: 5_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 500, max: 1_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_starwars_credit_pile_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-starwars-credit-pile-m',
    name: { pl: 'Stos Złota (Średni)', en: 'Gold Pile (Medium)' },
    biomeId: BIOME_STARWARS,
    hpBase: 20_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 2_500, max: 5_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_starwars_credit_pile_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-starwars-credit-pile-l',
    name: { pl: 'Stos Złota (Duży)', en: 'Gold Pile (Large)' },
    biomeId: BIOME_STARWARS,
    hpBase: 80_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 12_000, max: 25_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_starwars_credit_pile_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-starwars-imperial-crate',
    name: { pl: 'Skrzynia Dynamitu', en: 'Dynamite Crate' },
    biomeId: BIOME_STARWARS,
    hpBase: 50_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 6_000, max: 10_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_starwars_imperial_crate', destroyFx: 'stone' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-starwars-droid-scrap',
    name: { pl: 'Rozbity Wagonik', en: 'Broken Mine Cart' },
    biomeId: BIOME_STARWARS,
    hpBase: 250_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 40_000, max: 80_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_starwars_droid_scrap', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-starwars-kyber-crystal',
    name: { pl: 'Złoty Samorodek', en: 'Giant Gold Nugget' },
    biomeId: BIOME_STARWARS,
    hpBase: 600_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 150_000, max: 300_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_starwars_kyber_crystal', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PIXEL WORLD BIOME (tier 4) — HP ×1000, coin reward ×1000 vs meadow (×10 vs starwars).
  // PNG w `assets/biomes/pixel/destructibles/`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-pixel-coin-pile-s',
    name: { pl: 'Stosik Pixel-Monet (Mały)', en: 'Pixel Coin Pile (Small)' },
    biomeId: BIOME_PIXEL,
    hpBase: 50_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 5_000, max: 10_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_pixel_coin_pile_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-pixel-coin-pile-m',
    name: { pl: 'Stos Pixel-Monet (Średni)', en: 'Pixel Coin Pile (Medium)' },
    biomeId: BIOME_PIXEL,
    hpBase: 200_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 25_000, max: 50_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_pixel_coin_pile_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-pixel-coin-pile-l',
    name: { pl: 'Skarb Pixel-Monet (Duży)', en: 'Pixel Coin Pile (Large)' },
    biomeId: BIOME_PIXEL,
    hpBase: 800_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 120_000, max: 250_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_pixel_coin_pile_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-pixel-loot-block',
    name: { pl: 'Blok Skarbu', en: 'Loot Block' },
    biomeId: BIOME_PIXEL,
    hpBase: 500_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 60_000, max: 100_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_pixel_loot_block', destroyFx: 'stone' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-pixel-data-core',
    name: { pl: 'Rdzeń Danych', en: 'Data Core' },
    biomeId: BIOME_PIXEL,
    hpBase: 2_500_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 400_000, max: 800_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_pixel_data_core', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-pixel-mega-crystal',
    name: { pl: 'Mega Pixel-Kryształ', en: 'Mega Pixel Crystal' },
    biomeId: BIOME_PIXEL,
    hpBase: 6_000_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_500_000, max: 3_000_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_pixel_mega_crystal', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // OCEAN BIOME (tier 5) — HP ×10000, coin reward ×10000 vs meadow (×10 vs pixel).
  // PNG w `assets/biomes/ocean/destructibles/`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-ocean-pearl-pile-s',
    name: { pl: 'Kupka Pereł (Mała)', en: 'Pearl Pile (Small)' },
    biomeId: BIOME_OCEAN,
    hpBase: 500_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 50_000, max: 100_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_ocean_pearl_pile_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-ocean-pearl-pile-m',
    name: { pl: 'Kupka Pereł (Średnia)', en: 'Pearl Pile (Medium)' },
    biomeId: BIOME_OCEAN,
    hpBase: 2_000_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 250_000, max: 500_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_ocean_pearl_pile_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-ocean-pearl-pile-l',
    name: { pl: 'Kupka Pereł (Duża)', en: 'Pearl Pile (Large)' },
    biomeId: BIOME_OCEAN,
    hpBase: 8_000_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_200_000, max: 2_500_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_ocean_pearl_pile_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-ocean-treasure-chest',
    name: { pl: 'Zatopiona Skrzynia', en: 'Sunken Treasure Chest' },
    biomeId: BIOME_OCEAN,
    hpBase: 5_000_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 600_000, max: 1_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_ocean_treasure_chest', destroyFx: 'wood' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-ocean-giant-clam',
    name: { pl: 'Olbrzymi Małż', en: 'Giant Clam' },
    biomeId: BIOME_OCEAN,
    hpBase: 25_000_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 4_000_000, max: 8_000_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_ocean_giant_clam', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-ocean-coral-crystal',
    name: { pl: 'Koralowy Kryształ', en: 'Coral Crystal Cluster' },
    biomeId: BIOME_OCEAN,
    hpBase: 60_000_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 15_000_000, max: 30_000_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_ocean_coral_crystal', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FOOD BIOME (tier 6) — HP ×100000, coin reward ×100000 vs meadow (×10 vs ocean).
  // Motyw fast food: porcje frytek (coin piles), pudełko śniadaniowe (wood),
  // automat z przekąskami (stone, gems) i wielki tort (crystal mini-boss, gems).
  // PNG w `assets/biomes/food/destructibles/`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-food-fries-s',
    name: { pl: 'Frytki (Mała Porcja)', en: 'Fries (Small)' },
    biomeId: BIOME_FOOD,
    hpBase: 5_000_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 500_000, max: 1_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_food_fries_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-food-fries-m',
    name: { pl: 'Frytki (Średnia Porcja)', en: 'Fries (Medium)' },
    biomeId: BIOME_FOOD,
    hpBase: 20_000_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 2_500_000, max: 5_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_food_fries_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-food-fries-l',
    name: { pl: 'Frytki (Duża Porcja)', en: 'Fries (Large)' },
    biomeId: BIOME_FOOD,
    hpBase: 80_000_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 12_000_000, max: 25_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_food_fries_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-food-lunchbox',
    name: { pl: 'Pudełko Śniadaniowe', en: 'Lunchbox' },
    biomeId: BIOME_FOOD,
    hpBase: 50_000_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 6_000_000, max: 10_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_food_lunchbox', destroyFx: 'wood' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-food-vending',
    name: { pl: 'Automat z Przekąskami', en: 'Snack Vending Machine' },
    biomeId: BIOME_FOOD,
    hpBase: 250_000_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 40_000_000, max: 80_000_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_food_vending', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-food-cake',
    name: { pl: 'Wielki Tort', en: 'Giant Cake' },
    biomeId: BIOME_FOOD,
    hpBase: 600_000_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 150_000_000, max: 300_000_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (patrz backend DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_food_cake', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ATLANTYDA BIOME (tier 7) — HP ×1_000_000, coin reward ×1_000_000 vs meadow
  // (×10 vs food). Amfory ze złotem (coin), koralowa skrzynia (wood), posąg
  // strażnika (stone, gems), ołtarz trójzębu (crystal mini-boss, gems) + Sejf
  // Atlantydy (ATL-1, mega-cel, gems). PNG w `assets/biomes/atlantis/destructibles/`.
  // Gemy dla guardian-statue + trident-altar + vault nalicza serwer
  // (DESTRUCTIBLE_GEM_REWARDS w server/api/src/game-config/destructibles.ts).
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-atlantis-amphora-s',
    name: { pl: 'Amfora ze Złotem (Mała)', en: 'Gold Amphora (Small)' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 50_000_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 5_000_000, max: 10_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_atlantis_amphora_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-atlantis-amphora-m',
    name: { pl: 'Amfora ze Złotem (Średnia)', en: 'Gold Amphora (Medium)' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 200_000_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 25_000_000, max: 50_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_atlantis_amphora_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-atlantis-amphora-l',
    name: { pl: 'Amfora ze Złotem (Duża)', en: 'Gold Amphora (Large)' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 800_000_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 120_000_000, max: 250_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_atlantis_amphora_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-atlantis-coral-chest',
    name: { pl: 'Koralowa Skrzynia', en: 'Coral Chest' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 500_000_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 60_000_000, max: 100_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_atlantis_coral_chest', destroyFx: 'wood' },
    size: { w: 80, h: 72 },
  },
  {
    id: 'dest-atlantis-guardian-statue',
    name: { pl: 'Posąg Strażnika', en: 'Guardian Statue' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 2_500_000_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 400_000_000, max: 800_000_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_atlantis_guardian_statue', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-atlantis-trident-altar',
    name: { pl: 'Ołtarz Trójzębu', en: 'Trident Altar' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 6_000_000_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_500_000_000, max: 3_000_000_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_atlantis_trident_altar', destroyFx: 'crystal' },
    size: { w: 80, h: 112 },
  },
  {
    // ATL-1: Sejf Atlantydy — mega-cel w Skarbcu (6 petów, długo bić). ~5× HP
    // Ołtarza, najtłustsza nagroda monet + najlepsze gemy biomu (20%×4-8 server-side).
    id: 'dest-atlantis-vault',
    name: { pl: 'Sejf Atlantydy', en: 'Vault of Atlantis' },
    biomeId: BIOME_ATLANTIS,
    hpBase: 30_000_000_000,
    respawnSec: 180,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 15_000_000_000, max: 30_000_000_000, chance: 1.0 },
      // gems server-side: 20% × 4-8 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_atlantis_vault', destroyFx: 'crystal' },
    size: { w: 112, h: 96 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SZKOŁA BIOME (tier 8) — bazowe cele i coin reward ×10_000_000 vs meadow
  // (×10 vs Atlantyda). Trzy kotwice mapy mają wyższe HP, bo 4× Posejdon Huge
  // z poprzedniego biomu zadają 250 mld w pierwszej salwie i ~500 mld DPS.
  // Stosy zeszytów (coin), piórnik (wood), plecak (stone, gems), globus
  // (crystal mini-boss, gems) + Dzwonek Szkolny (mega-cel, gems).
  // PNG w `assets/biomes/school/destructibles/`.
  // Gemy dla backpack + globe + school-bell nalicza serwer
  // (DESTRUCTIBLE_GEM_REWARDS w server/api/src/game-config/destructibles.ts).
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-school-notebook-stack-s',
    name: { pl: 'Stos Zeszytów (Mały)', en: 'Notebook Stack (Small)' },
    biomeId: BIOME_SCHOOL,
    hpBase: 500_000_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 50_000_000, max: 100_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_school_notebook_stack_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-school-notebook-stack-m',
    name: { pl: 'Stos Zeszytów (Średni)', en: 'Notebook Stack (Medium)' },
    biomeId: BIOME_SCHOOL,
    hpBase: 2_000_000_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 250_000_000, max: 500_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_school_notebook_stack_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-school-notebook-stack-l',
    name: { pl: 'Stos Zeszytów (Duży)', en: 'Notebook Stack (Large)' },
    biomeId: BIOME_SCHOOL,
    hpBase: 8_000_000_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_200_000_000, max: 2_500_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_school_notebook_stack_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-school-pencil-case',
    name: { pl: 'Piórnik', en: 'Pencil Case' },
    biomeId: BIOME_SCHOOL,
    hpBase: 5_000_000_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 600_000_000, max: 1_000_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_school_pencil_case', destroyFx: 'wood' },
    size: { w: 88, h: 56 },
  },
  {
    id: 'dest-school-backpack',
    name: { pl: 'Plecak Pierwszaka', en: "First-Grader's Backpack" },
    biomeId: BIOME_SCHOOL,
    // ~0,5 s przeciw 4× Posejdon Huge (62,5 mld/hit, tick co 500 ms).
    hpBase: 400_000_000_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 4_000_000_000, max: 8_000_000_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_school_backpack', destroyFx: 'stone' },
    size: { w: 88, h: 96 },
  },
  {
    id: 'dest-school-globe',
    name: { pl: 'Globus', en: 'Classroom Globe' },
    biomeId: BIOME_SCHOOL,
    // ~2 s przeciw 4× Posejdon Huge — mini-boss nie znika od jednego peta.
    hpBase: 1_250_000_000_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 15_000_000_000, max: 30_000_000_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_school_globe', destroyFx: 'crystal' },
    size: { w: 80, h: 96 },
  },
  {
    // Mega-cel biomu (odpowiednik Sejfu Atlantydy) — 6 petów, długo bić.
    // 3,2× HP Globusa; ~7,5 s przeciw 4× Posejdon Huge bez mikstury siły.
    id: 'dest-school-school-bell',
    name: { pl: 'Dzwonek Szkolny', en: 'Great School Bell' },
    biomeId: BIOME_SCHOOL,
    hpBase: 4_000_000_000_000,
    respawnSec: 180,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 150_000_000_000, max: 300_000_000_000, chance: 1.0 },
      // gems server-side: 20% × 4-8 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_school_school_bell', destroyFx: 'crystal' },
    size: { w: 96, h: 104 },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ARKTYKA (tier 9) — HP i monety ×10 vs Szkoła. Trzy kotwice mapy
  // (Skrzynia Ekspedycji, Kryształ Zorzy, Prastara Góra Lodowa) mają gemy
  // naliczane wyłącznie po stronie serwera i związane z aktywnym biomem gracza.
  // PNG/prompty: assets/biomes/arctic/destructibles/.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'dest-arctic-snowdrift-s',
    name: { pl: 'Zaspa (Mała)', en: 'Snowdrift (Small)' },
    biomeId: BIOME_ARCTIC,
    hpBase: 5_000_000_000,
    respawnSec: 8,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 500_000_000, max: 1_000_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_arctic_snowdrift_s', destroyFx: 'coin' },
    size: { w: 64, h: 48 },
  },
  {
    id: 'dest-arctic-snowdrift-m',
    name: { pl: 'Zaspa (Średnia)', en: 'Snowdrift (Medium)' },
    biomeId: BIOME_ARCTIC,
    hpBase: 20_000_000_000,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 2_500_000_000, max: 5_000_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_arctic_snowdrift_m', destroyFx: 'coin' },
    size: { w: 80, h: 64 },
  },
  {
    id: 'dest-arctic-snowdrift-l',
    name: { pl: 'Zaspa (Duża)', en: 'Snowdrift (Large)' },
    biomeId: BIOME_ARCTIC,
    hpBase: 80_000_000_000,
    respawnSec: 30,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 12_000_000_000, max: 25_000_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_arctic_snowdrift_l', destroyFx: 'coin' },
    size: { w: 96, h: 80 },
  },
  {
    id: 'dest-arctic-frozen-fish-crate',
    name: { pl: 'Skrzynia Mrożonych Ryb', en: 'Frozen Fish Crate' },
    biomeId: BIOME_ARCTIC,
    hpBase: 50_000_000_000,
    respawnSec: 25,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 6_000_000_000, max: 10_000_000_000, chance: 1.0 },
    ],
    art: { textureKey: 'dest_arctic_frozen_fish_crate', destroyFx: 'wood' },
    size: { w: 88, h: 56 },
  },
  {
    id: 'dest-arctic-expedition-chest',
    name: { pl: 'Skrzynia Ekspedycji', en: 'Expedition Chest' },
    biomeId: BIOME_ARCTIC,
    hpBase: 4_000_000_000_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 40_000_000_000, max: 80_000_000_000, chance: 1.0 },
      // gems server-side: 6% × 2-3 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_arctic_expedition_chest', destroyFx: 'stone' },
    size: { w: 96, h: 88 },
  },
  {
    id: 'dest-arctic-aurora-crystal',
    name: { pl: 'Kryształ Zorzy', en: 'Aurora Crystal' },
    biomeId: BIOME_ARCTIC,
    hpBase: 12_500_000_000_000,
    respawnSec: 90,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 150_000_000_000, max: 300_000_000_000, chance: 1.0 },
      // gems server-side: 16% × 3-6 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_arctic_aurora_crystal', destroyFx: 'crystal' },
    size: { w: 88, h: 108 },
  },
  {
    // Mega-cel biomu: wielka błękitna góra lodowa ze skarbem uwięzionym w środku.
    id: 'dest-arctic-ancient-iceberg',
    name: { pl: 'Prastara Góra Lodowa', en: 'Ancient Iceberg' },
    biomeId: BIOME_ARCTIC,
    hpBase: 40_000_000_000_000,
    respawnSec: 180,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 1_500_000_000_000, max: 3_000_000_000_000, chance: 1.0 },
      // gems server-side: 20% × 4-8 (DESTRUCTIBLE_GEM_REWARDS)
    ],
    art: { textureKey: 'dest_arctic_ancient_iceberg', destroyFx: 'crystal' },
    size: { w: 120, h: 112 },
  },

  // ════════════════════════════════════════════════════════════════════════════
  // EVENT „WAKACJE CAPIBAR" — Plaża Capibar (EV-1b)
  // Wartości monet UMIARKOWANE (event dla każdego, też słabego konta — NIE tier
  // drabinki). Muszelki (event_shells) NIE są tu — to waluta server-authoritative
  // naliczana SERWEROWO (model gemów, EV-1b-2: DESTRUCTIBLE_SHELL_REWARDS), tak
  // jak gemy nie są w klienckim `rewards`. Liczby do strojenia w configu bez deployu.
  // ════════════════════════════════════════════════════════════════════════════
  {
    // łatwy — muszelki S (server-side)
    id: 'dest-beach-sandcastle',
    name: { pl: 'Zamek z piasku', en: 'Sandcastle' },
    biomeId: BIOME_EVENT_BEACH,
    hpBase: 120,
    respawnSec: 6,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 20, max: 40, chance: 1.0 },
      // muszelki S server-side (EV-1b-2)
    ],
    art: { textureKey: 'dest_beach_sandcastle', destroyFx: 'wood' },
    size: { w: 78, h: 68 },
  },
  {
    // łatwy — muszelki S (server-side)
    id: 'dest-beach-bucket',
    name: { pl: 'Wiaderko i foremki', en: 'Bucket and Molds' },
    biomeId: BIOME_EVENT_BEACH,
    hpBase: 120,
    respawnSec: 6,
    maxPetSlots: 3,
    rewards: [
      { currency: 'coins', min: 20, max: 40, chance: 1.0 },
      // muszelki S server-side (EV-1b-2)
    ],
    art: { textureKey: 'dest_beach_bucket', destroyFx: 'wood' },
    size: { w: 62, h: 62 },
  },
  {
    // średni — GŁÓWNE źródło muszelek (M, server-side)
    id: 'dest-beach-shellpile',
    name: { pl: 'Sterta muszli', en: 'Shell Pile' },
    biomeId: BIOME_EVENT_BEACH,
    hpBase: 400,
    respawnSec: 12,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 60, max: 120, chance: 1.0 },
      // muszelki M server-side (EV-1b-2) — główne źródło
    ],
    art: { textureKey: 'dest_beach_shellpile', destroyFx: 'stone' },
    size: { w: 78, h: 58 },
  },
  {
    // średni — muszelki M + monety (server-side muszelki)
    id: 'dest-beach-chest',
    name: { pl: 'Skrzynia plażowa', en: 'Beach Chest' },
    biomeId: BIOME_EVENT_BEACH,
    hpBase: 500,
    respawnSec: 15,
    maxPetSlots: 4,
    rewards: [
      { currency: 'coins', min: 100, max: 200, chance: 1.0 },
      // muszelki M server-side (EV-1b-2)
    ],
    art: { textureKey: 'dest_beach_chest', destroyFx: 'wood' },
    size: { w: 78, h: 64 },
  },
  {
    // trudny — muszelki L + monety L + szansa na 💎 (gemy + muszelki server-side)
    id: 'dest-beach-treasure',
    name: { pl: 'Kufer skarbów', en: 'Treasure Chest' },
    biomeId: BIOME_EVENT_BEACH,
    hpBase: 1_500,
    respawnSec: 30,
    maxPetSlots: 5,
    rewards: [
      { currency: 'coins', min: 400, max: 800, chance: 1.0 },
      // muszelki L + gemy server-side (EV-1b-2)
    ],
    art: { textureKey: 'dest_beach_treasure', destroyFx: 'coin' },
    size: { w: 96, h: 76 },
  },
  {
    // trudny — mini-boss, muszelki L + 💎 (wymusza eksplorację; server-side)
    id: 'dest-beach-giant-shell',
    name: { pl: 'Wielka muszla', en: 'Giant Shell' },
    biomeId: BIOME_EVENT_BEACH,
    hpBase: 3_000,
    respawnSec: 60,
    maxPetSlots: 6,
    rewards: [
      { currency: 'coins', min: 800, max: 1_500, chance: 1.0 },
      // muszelki L + gemy server-side (EV-1b-2)
    ],
    art: { textureKey: 'dest_beach_giant_shell', destroyFx: 'crystal' },
    size: { w: 116, h: 106 },
  },
  ...halloween.pumpkins.map((pumpkin): DestructibleDef => ({
    id: pumpkin.id,
    name: { pl: pumpkin.name, en: pumpkin.name },
    biomeId: halloween.event.biomeId,
    hpBase: pumpkin.hp,
    respawnSec: pumpkin.respawnSec,
    maxPetSlots: 6,
    rewards: [{ currency: 'coins', min: 5, max: 10, chance: 1 }],
    art: {
      textureKey: pumpkin.textureKey,
      destroyFx: 'wood',
      showLabel: false,
      idleGlow: { color: 0xffe6ac, alpha: 0.58 },
    },
    size: { w: pumpkin.size, h: pumpkin.size },
  })),
];

/** Mapa pomocnicza id → DestructibleDef. */
export const DESTRUCTIBLES_BY_ID: Record<string, DestructibleDef> = Object.fromEntries(
  DESTRUCTIBLES.map((d) => [d.id, d]),
);
